import React from 'react';
import ReactDOM from 'react-dom';

//import 'bnet/css/tooltips.css';
import './Tooltip.scss';

function getTooltipRoot() {
  let node = document.getElementById("laplanner-tooltip-root");
  if (node) return node;
  node = document.createElement("div");
  node.id = "laplanner-tooltip-root";
  document.body.appendChild(node);
  return node;
}

function scrollTooltip(evt, tooltip, tipRect, scrollPos) {
  const viewport = {
    left: 5,
    top: 5,
    right: document.documentElement.clientWidth - 5,
    bottom: document.documentElement.clientHeight - 5,
  };

  let clipped = false;
  function clip(val, min, max) {
    if (val < min) {
      val = min;
      clipped = true;
    }
    if (val > max) {
      val = max;
      clipped = true;
    }
    return val;
  }

  if (tipRect.right - tipRect.left > viewport.right - viewport.left) {
    scrollPos.x = clip(scrollPos.x + evt.deltaX, tipRect.left - viewport.left, tipRect.right - viewport.right);
  }
  if (tipRect.bottom - tipRect.top > viewport.bottom - viewport.top) {
    scrollPos.y = clip(scrollPos.y + evt.deltaY, tipRect.top - viewport.top, tipRect.bottom - viewport.bottom);
  }

  tooltip.style.left = (tipRect.left - scrollPos.x) + "px";
  tooltip.style.top = (tipRect.top - scrollPos.y) + "px";

  if (!clipped) {
    evt.preventDefault();
    evt.stopPropagation();
  }
}

function positionTooltip(tooltip, node, align, padding, scrollPos) {
  const rect = node.getBoundingClientRect();
  const viewport = {
    left: 10,
    top: 10,
    right: document.documentElement.clientWidth - 10,
    bottom: document.documentElement.clientHeight - 10,
  };

  const order = ["right", "left", "top", "bottom"];
  if (align) {
    const index = order.indexOf(align);
    if (index >= 0) {
      order[index] = order[0];
      order[0] = align;
    }
  }
  let padX = 5, padY = 5;
  if (padding) {
    padX += padding;
    padY += padding;
  }

  const width = tooltip.offsetWidth;
  const height = tooltip.offsetHeight;
  let horzY = Math.round((rect.top + rect.bottom - height) / 2);
  horzY = Math.min(horzY, viewport.bottom - height);
  horzY = Math.max(horzY, viewport.top);
  let vertX = Math.round((rect.left + rect.right - width) / 2);
  vertX = Math.min(vertX, viewport.right - width);
  vertX = Math.max(vertX, viewport.left);

  let x, y, side;
  for (let i = 0; i < order.length; ++i) {
    if (order[i] === "right") {
      if (rect.right + padX + width <= viewport.right) {
        x = rect.right + padX;
        y = horzY;
        side = "right";
        break;
      }
    } else if (order[i] === "left") {
      if (rect.left - padX - width >= viewport.left) {
        x = rect.left - padX - width;
        y = horzY;
        side = "left";
        break;
      }
    } else if (order[i] === "top") {
      if (rect.top - padY - height >= viewport.top) {
        x = vertX;
        y = rect.top - padY - height;
        side = "top";
        break;
      }
    } else if (order[i] === "bottom") {
      if (rect.bottom + padY + height <= viewport.bottom) {
        x = vertX;
        y = rect.bottom + padY;
        side = "bottom";
        break;
      }
    }
  }
  let overflow = false;
  if (!side) {
    if (viewport.right - rect.right > rect.left - viewport.left) {
      x = rect.right + padX;
      y = horzY;
      side = "right";
    } else {
      x = rect.left - padY - width;
      y = horzY;
      side = "left";
    }
    overflow = true;
  }

  const tipRect = {
    left: x,
    top: y,
    right: x + width,
    bottom: y + height,
    scroll: (width > viewport.right - viewport.left) || (height > viewport.bottom - viewport.top),
    overflow,
  };

  if (width > viewport.right - viewport.left) {
    scrollPos.x = Math.max(Math.min(scrollPos.x, tipRect.right - viewport.right), tipRect.left - viewport.left);
  }
  if (height > viewport.bottom - viewport.top) {
    scrollPos.y = Math.max(Math.min(scrollPos.y, tipRect.bottom - viewport.bottom), tipRect.top - viewport.top);
  }

  tooltip.style.left = (x - scrollPos.x) + "px";
  tooltip.style.top = (y - scrollPos.y) + "px";
  tooltip.className = `la-tooltip-positioner la-tooltip-visible la-tooltip-${side}`;
  //tooltip.style.visibility = "visible";

  return tipRect;
}

function TooltipBody({ tooltip, nodeRef, align, padding, touch, onClose }) {
  const [fullscreen, setFullscreen] = React.useState(false);
  const outer = React.useRef();
  const wrapper = React.useRef();
  const tipRect = React.useRef({scroll: false});
  const tipScroll = React.useRef({x: 0, y: 0});

  const onScroll = React.useCallback(evt => {
    const wrapperNode = wrapper.current && ReactDOM.findDOMNode(wrapper.current);
    if (wrapperNode && tipRect.current.scroll) {
      scrollTooltip(evt, wrapper.current, tipRect.current, tipScroll.current);
    }
  }, []);

  // eslint-disable-next-line
  React.useLayoutEffect(() => {
    if (!fullscreen) {
      const targetNode = nodeRef.current && ReactDOM.findDOMNode(nodeRef.current);
      if (targetNode) {
        tipRect.current = positionTooltip(wrapper.current, targetNode, align, padding, tipScroll.current);
        const link = touch && targetNode.closest("a.lap-PaperdollContainer")?.href;
        if (touch && (tipRect.current.overflow || link)) {
          setFullscreen(link || true);
        }
      }
    }
  });

  React.useEffect(() => {
    const targetNode = nodeRef.current && ReactDOM.findDOMNode(nodeRef.current);
    if (targetNode) {
      targetNode.addEventListener("wheel", onScroll, {passive: false});
      return () => targetNode.removeEventListener("wheel", onScroll);
    }
  }, [nodeRef, onScroll]);

  React.useEffect(() => {
    if (touch && !fullscreen) {
      function onClick(e) {
        if (!wrapper.current.contains(e.target) && onClose) {
          onClose();
        }
      }
      document.addEventListener("touchstart", onClick, true);
      return () => document.removeEventListener("touchstart", onClick, true);
    }
  }, [touch, onClose, fullscreen]);

  const style = {};//visibility: "hidden"};
  if (touch) style.pointerEvents = "auto";

  const isFullscreen = !!fullscreen;
  React.useLayoutEffect(() => {
    if (isFullscreen) {
      const next = wrapper.current.querySelector(".la-tooltip-wrapper");
      if (!next) return;
      const rect = next.getBoundingClientRect();
      const height = document.documentElement.clientHeight; 
      // hardcoded padding
      if (rect.bottom - rect.top < height - 60) {
        next.style.marginTop = Math.round((height - 60 - (rect.bottom - rect.top)) / 2) + "px";
      }
    }
  }, [isFullscreen]);

  return (
    <div className={"laplanner-tooltip" + (fullscreen ? " laplanner-fullscreen" : "")} ref={outer}>
      <div className="la-tooltip-positioner" ref={wrapper} style={style}>
        {tooltip}
      </div>
      {isFullscreen && <span className="lap-tooltip-close" onClick={onClose}/>}
      {isFullscreen && fullscreen !== true && (
        <a className="lap-tooltip-link" href={fullscreen} target="_blank" rel="noopener noreferrer" onClick={onClose}>Open in laPlanner</a>
      )}
    </div>
  );
}

class RefHolder extends React.Component {
  render() {
    return this.props.children;
  }
}

const activeTips = new Map();
let currentTip = null;
function createTooltip(node, onShow, onHide) {
  if (activeTips.has(node)) return activeTips.get(node).unregister;
  const unregister = () => {
    onHide();
    activeTips.delete(node);
    if (currentTip === node) {
      currentTip = null;
      for (const node of activeTips.keys()) {
        if (!currentTip || !node.contains(currentTip)) currentTip = node;
      }
      if (currentTip) {
        activeTips.get(currentTip)?.onShow();
      }
    }
  };
  activeTips.set(node, {onShow, onHide, unregister});
  if (!currentTip || !node.contains(currentTip)) {
    activeTips.get(currentTip)?.onHide();
    onShow();
    currentTip = node;
  }
  return unregister;
}

function TooltipInner({ children, tooltip, align, padding, disabled, touchAction }) {
  const nodeRef = React.useRef();
  const [show, setShow] = React.useState(false);

  const observer = React.useRef();
  const root = React.useRef();
  const target = React.useRef();
  const tracker = React.useRef();

  const onPointerLeave = React.useCallback(() => {
    if (observer.current && root.current) {
      observer.current.disconnect();
      root.current = null;
      target.current = null;
    }
    if (tracker.current) {
      tracker.current();
      tracker.current = null;
    }
  }, []);

  const onPointerEnter = React.useCallback(e => {
    if (e.pointerType !== 'mouse' && touchAction === 'none') return;
    if (!observer.current && window.MutationObserver) {
      observer.current = new MutationObserver(() => {
        if (root.current && target.current && !root.current.contains(target.current)) {
          onPointerLeave();
        }
      });
    }
    const node = ReactDOM.findDOMNode(nodeRef.current);
    if (observer.current) {
      target.current = e.target;
      root.current = node;
      observer.current.observe(root.current, {childList: true, subtree: true});
    }
    tracker.current = createTooltip(node, () => setShow(true), () => setShow(false));
  }, [onPointerLeave, touchAction]);

  React.useEffect(() => {
    return () => {
      if (observer.current) {
        // eslint-disable-next-line
        observer.current.disconnect();
      }
      if (tracker.current) {
        tracker.current();
      }
    };
  }, []);

  const touchPos = React.useRef();
  const onTouchStart = React.useCallback(e => {
    if (touchAction === 'none') return;
    if (e.touches?.length === 1) {
      const t = e.touches[0];
      touchPos.current = {x: t.clientX, y: t.clientY};
    } else {
      touchPos.current = null;
    }
  }, [touchAction]);
  const onTouchEnd = React.useCallback(e => {
    if (touchAction !== 'none') {
      e.preventDefault();
      if (e.touches?.length === 0 && e.changedTouches?.length === 1 && touchPos.current) {
        const t = e.changedTouches[0];
        const dx = Math.abs(t.clientX - touchPos.current.x);
        const dy = Math.abs(t.clientY - touchPos.current.y);
        if (dx < 50 && dy < 50) {
          const node = ReactDOM.findDOMNode(nodeRef.current);
          tracker.current = createTooltip(node, () => setShow('touch'), () => setShow(false));
        }
      }
    }
    touchPos.current = null;
  }, [setShow, touchAction]);

  const child = React.Children.only(children);
  const handlers = {onPointerEnter, onPointerLeave, onTouchStart, onTouchEnd};
  for (const type in handlers) {
    if (child.props[type]) {
      const orig = handlers[type], other = child.props[type];
      handlers[type] = function() {
        orig.apply(this, arguments);
        other.apply(this, arguments);
      };
    }
  }
  return (
    <React.Fragment>
      <RefHolder ref={nodeRef}>
        {React.cloneElement(child, handlers)}
      </RefHolder>
      {!!show && !disabled && !!tooltip && ReactDOM.createPortal(
        <TooltipBody tooltip={tooltip} nodeRef={nodeRef} align={align} padding={padding} onClose={onPointerLeave} touch={show === "touch"}/>,
        getTooltipRoot()
      )}
    </React.Fragment>
  );
}

export default function Tooltip({ tooltip, touchAction, ...props }) {
  tooltip = (
    <div className="la-tooltip-wrapper la-tooltip-plain">
      <div className="lap-tooltip-content">{tooltip}</div>
    </div>
  );
  return <TooltipInner {...props} tooltip={tooltip} touchAction={touchAction || 'none'}/>;
}

export function RawTooltip({ tooltip, ...props }) {
  return <TooltipInner {...props} tooltip={tooltip}/>;
}
